import { Link } from 'wouter';
import { Logo, MonoLabel } from '../components/primitives/index.js';

function LegalShell({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-8 md:py-12 max-w-3xl mx-auto">
        <MonoLabel>Legal</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium mt-3 mb-6 md:mb-8">{title}</h1>
        <div className="prose-content space-y-5 text-fg-2 leading-relaxed text-[15px]">
          {children}
        </div>
        <div className="mt-12 pt-6 border-t border-line text-sm text-muted">
          Ai întrebări? Scrie-ne la{' '}
          <a className="text-fg underline" href="mailto:gdpr@anuleaza.ro">
            gdpr@anuleaza.ro
          </a>
          .{' · '}
          <Link href="/" className="underline">
            ← Pagina principală
          </Link>
        </div>
      </main>
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-medium mt-8 mb-3 text-fg">{children}</h2>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Politica de confidențialitate">
      <p>
        Anulează.ro respectă Regulamentul general privind protecția datelor (GDPR, Regulamentul UE
        2016/679) și legislația română în domeniu. Această politică explică ce date colectăm, cum
        le folosim și ce drepturi ai.
      </p>

      <H2>Operatorul de date</H2>
      <p>
        Operator: Anulează.ro (în curs de înregistrare ca persoană juridică). Contact GDPR:{' '}
        <a className="underline" href="mailto:gdpr@anuleaza.ro">gdpr@anuleaza.ro</a>.
      </p>

      <H2>Ce date colectăm</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Email — pentru autentificare și livrarea plângerii.</li>
        <li>
          Fotografia procesului-verbal (PV) — stocată în Cloudflare R2, criptată, ștearsă automat
          după 90 zile.
        </li>
        <li>
          Date extrase din PV de AI (fapta, articolele, agentul, sancțiunea) — folosite pentru
          generarea plângerii. <strong>NU extragem CNP-ul.</strong>
        </li>
        <li>
          Răspunsurile pe care le dai în chestionarul de circumstanțe — folosite pentru analiza
          juridică.
        </li>
        <li>Plângerea PDF generată — păstrată în contul tău, descărcabilă 30 zile.</li>
      </ul>

      <H2>Cum folosim datele</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Pentru a genera plângerea contravențională pe care o vei depune la judecătorie.</li>
        <li>Pentru a-ți permite să vezi istoricul dosarelor în dashboard.</li>
        <li>
          Pentru a îmbunătăți modelele AI (date agregate, anonimizate). Te poți retrage oricând.
        </li>
      </ul>

      <H2>Drepturile tale (GDPR)</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Acces — poți cere o copie a datelor tale.</li>
        <li>Rectificare — poți corecta date incorecte.</li>
        <li>Ștergere — poți cere ștergerea contului și a tuturor datelor.</li>
        <li>Restricționare — poți cere oprirea procesării temporare.</li>
        <li>Portabilitate — poți primi datele într-un format structurat.</li>
        <li>Opoziție — poți refuza prelucrarea pentru marketing.</li>
      </ul>
      <p>
        Pentru exercitarea oricărui drept, scrie la{' '}
        <a className="underline" href="mailto:gdpr@anuleaza.ro">gdpr@anuleaza.ro</a>. Răspundem în
        cel mult 30 zile.
      </p>

      <H2>Cookies & tracking</H2>
      <p>
        Folosim doar cookie tehnic strict necesar pentru autentificare (sesiune semnată JWT). Nu
        folosim cookies de marketing sau tracking third-party. Nu vindem date.
      </p>

      <H2>Securitate</H2>
      <p>
        Toate datele circulă prin HTTPS. Fotografiile PV-ului sunt stocate în R2 cu acces
        restricționat prin URL-uri semnate (5 min validitate). Datele PII din baza de date sunt
        criptate AES-GCM cu chei rotite trimestrial. Logăm acceasele administrative.
      </p>

      <H2>Modificări</H2>
      <p>
        Dacă schimbăm această politică în mod substanțial, te anunțăm pe email cu cel puțin 30
        zile înainte. Ultima actualizare: 2026-05-16.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Termeni și condiții">
      <p>
        Prin folosirea anuleaza.ro, ești de acord cu acești termeni. Te rugăm să-i citești cu
        atenție — sunt scriși în română clară, nu în legalize.
      </p>

      <H2>Ce face serviciul</H2>
      <p>
        Anulează.ro generează plângeri contravenționale (formulare juridice) bazate pe procesul-
        verbal pe care îl încarci și pe răspunsurile pe care le dai. Folosim AI și o bază de date
        de spețe câștigate ca să identificăm motive de nulitate.
      </p>

      <H2>Ce NU face serviciul</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>
          Nu suntem cabinet de avocatură. Plângerea generată este un document pe care îl depui
          tu, sub semnătura ta, la judecătorie.
        </li>
        <li>Nu garantăm câștigarea procesului — instanța decide.</li>
        <li>
          Nu te reprezentăm în instanță. Dacă vrei reprezentare, contactează un avocat partener.
        </li>
      </ul>

      <H2>Plată și garanție</H2>
      <p>
        Prețul este <strong>49 lei</strong>, plătit prin Stripe doar după ce plângerea este gata.
        Garanție 30 zile: dacă pierzi în primă instanță, îți returnăm integral suma. Trimite
        sentința pe email și procesăm refund-ul în 5 zile lucrătoare.
      </p>

      <H2>Răspunsuri corecte</H2>
      <p>
        Răspunsurile pe care le dai în chestionar trebuie să fie adevărate. Plângerea va cita
        aceste răspunsuri ca fapte. Dacă minți, riști sancțiune pentru fals în declarații —
        nu noi.
      </p>

      <H2>Limitarea răspunderii</H2>
      <p>
        Anulează.ro nu răspunde pentru daune indirecte rezultate din utilizarea serviciului
        (ex: amenda nu se anulează, dosar pierdut din culpa instanței, etc.). Răspunderea totală
        este limitată la suma plătită (49 lei).
      </p>

      <H2>Modificarea termenilor</H2>
      <p>
        Putem schimba acești termeni; te anunțăm pe email cu 14 zile înainte. Dacă nu ești de
        acord, poți cere ștergerea contului fără cost.
      </p>

      <H2>Jurisdicție</H2>
      <p>
        Litigiile se soluționează amiabil prin medierea Autorității Naționale pentru Protecția
        Consumatorilor (ANPC). În subsidiar, competente sunt instanțele române.
      </p>

      <p className="text-sm text-muted">Ultima actualizare: 2026-05-16.</p>
    </LegalShell>
  );
}
